/**
 * Traffic across the star chart: real craft, not outlines.
 *
 * WHY RASTER NOW. The first version drew vector silhouettes, and rendering them at the sizes they
 * actually fly at settled it: the interceptor outline read fine and the fighter came out as an
 * ambiguous asterisk. He called the whole thing basic, and he was right about the reason rather
 * than only the result. A hull with panel lines, shadow and an engine glow reads as a SHIP at forty
 * pixels; a stroked outline reads as a diagram at any size. Three sprites cost 29KB together,
 * which is less than the emblem this deck already precaches.
 *
 * WHY IT IS STILL CHEAP. `force-graph` already runs a canvas loop with `autoPauseRedraw(false)` for
 * the hover highlight, and already pauses on `visibilitychange` and when scrolled out of view. So
 * this rides frames that were being spent anyway, `drawImage` of a 132px sprite is one blit, and it
 * stops when the tab does. That is the only version of ambient motion that belongs in an installed
 * PWA on a phone.
 *
 * SCREEN SPACE, and BEHIND the nodes. The starfield is the PLACE the chart sits in, so it pans and
 * zooms with the nodes; a ship is TRAFFIC crossing the view, so it crosses the view at a constant
 * size. And it is painted in `onRenderFramePre` so it never passes over a node he is reading: the
 * chart is the content and this is scenery.
 */

export interface Ships {
  draw(ctx: CanvasRenderingContext2D, nodeCount: number): void;
}

interface Kind {
  src: string;
  /**
   * True when the rendered sprite's nose points LEFT.
   *
   * He spotted this on the live graph: every ship was flying backwards. The draw code assumed all
   * sprites face right and mirrored for leftward travel, and the generator had in fact produced the
   * Falcon and the X-wing nose-left, so BOTH directions came out reversed. Recorded per craft
   * rather than fixed by flipping the image files, because a convention baked invisibly into a
   * binary is the kind of thing the next sprite quietly breaks.
   */
  facesLeft?: boolean;
  /** Rendered width in CSS pixels at scale 1. Tuned per craft, not shared: the interceptor is as
   *  tall as it is wide and would dwarf the freighter at a common width. */
  w: number;
  /** How often this one turns up, relative to the others. */
  weight: number;
}

const KINDS: Kind[] = [
  // Head-on and symmetric, so its facing is direction-neutral.
  { src: '/ship-interceptor.webp', w: 46, weight: 5 },
  { src: '/ship-fighter.webp', w: 60, weight: 3, facesLeft: true },
  // The freighter is the rare one. A crossing should feel like something turning up.
  { src: '/ship-freighter.webp', w: 82, weight: 1, facesLeft: true },
];

interface Ship {
  img: HTMLImageElement;
  w: number;
  t: number;
  y: number;
  speed: number;
  dir: 1 | -1;
  facesLeft: boolean;
  scale: number;
  alpha: number;
  /** A slight bank, so it is flying rather than sliding. Radians. */
  tilt: number;
}

export function makeShips(): Ships {
  const still = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  /*
   * Loaded once, eagerly, and a sprite that fails simply never flies. No error surfaces: this is
   * scenery, and a missing decoration must never be something he has to think about.
   */
  const loaded = KINDS.map((k) => {
    const img = new Image();
    img.src = k.src;
    return { ...k, img };
  });

  const ships: Ship[] = [];
  let nextSpawn = performance.now() + 3500;
  let last = performance.now();

  const pick = (): (typeof loaded)[number] => {
    const total = loaded.reduce((n, k) => n + k.weight, 0);
    let r = Math.random() * total;
    for (const k of loaded) {
      r -= k.weight;
      if (r <= 0) return k;
    }
    return loaded[0]!;
  };

  const spawn = (): Ship | null => {
    const k = pick();
    if (!k.img.complete || k.img.naturalWidth === 0) return null;
    return {
      img: k.img,
      w: k.w,
      t: 0,
      y: 0.12 + Math.random() * 0.74,
      // Between roughly twenty and fifty seconds to cross. This is depth, not action.
      speed: 0.02 + Math.random() * 0.03,
      dir: Math.random() < 0.5 ? 1 : -1,
      facesLeft: k.facesLeft === true,
      scale: 0.8 + Math.random() * 0.8,
      /*
       * Deliberately more present than the first pass. Rendering the scene at flight size showed
       * the ships reading as smudges at 0.3, and he had already told me the effect was too subtle
       * to notice. Scenery that nobody sees is not restraint, it is waste. Still under full opacity,
       * because they sit behind the nodes and must never compete with a label.
       */
      alpha: 0.5 + Math.random() * 0.3,
      tilt: (Math.random() - 0.5) * 0.16,
    };
  };

  return {
    draw(ctx, nodeCount) {
      if (still) return;

      const now = performance.now();
      // Clamped, so a tab that stalled for a minute does not teleport a ship across the view.
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // Above a few thousand nodes the force layout is already the expensive thing, so the
      // scenery yields to it rather than competing.
      const cap = nodeCount > 4000 ? 1 : 2;
      if (ships.length < cap && now > nextSpawn) {
        const s = spawn();
        if (s !== null) ships.push(s);
        // Long gaps deliberately. An empty sky is what makes a crossing register at all.
        nextSpawn = now + 9000 + Math.random() * 15000;
      }

      const dpr = globalThis.devicePixelRatio || 1;
      const w = ctx.canvas.width / dpr;
      const h = ctx.canvas.height / dpr;

      ctx.save();
      // Out of graph space, into CSS pixels. See the docblock for why these are not in graph space.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      for (let i = ships.length - 1; i >= 0; i -= 1) {
        const s = ships[i]!;
        s.t += s.speed * dt;
        if (s.t > 1.25) {
          ships.splice(i, 1);
          continue;
        }
        const x = (s.dir === 1 ? -0.12 + s.t * 1.24 : 1.12 - s.t * 1.24) * w;
        const y = s.y * h + Math.sin(s.t * 3) * 7;
        const dw = s.w * s.scale;
        const dh = dw * (s.img.naturalHeight / s.img.naturalWidth);

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(s.tilt * s.dir);
        /*
         * Point the nose along the direction of travel. A left-facing sprite needs mirroring to fly
         * RIGHT, which is the opposite of what this did before.
         */
        ctx.scale(s.facesLeft ? -s.dir : s.dir, 1);
        ctx.globalAlpha = s.alpha;
        ctx.drawImage(s.img, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();
      }
      ctx.restore();
    },
  };
}
